import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { TrackingEvent } from '~/lib/types/tracking-types';
import { P2GGrid, P2GText } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';

interface ITrackingWindowProps {
  event: TrackingEvent;
  started?: boolean;
  headingVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const useStyles = makeStyles()(() => ({
  textSection: {
    flexDirection: 'column',
    justifyDirection: 'flex-start',
    alignItems: 'stretch',
    marginBottom: '8px'
  }
}));

const TrackingWindow: React.FC<ITrackingWindowProps> = (props) => {
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const start = React.useMemo(() => {
    if (props.event.windowStart) {
      return new Date(props.event.windowStart);
    }
    return null;
  }, [props.event]);

  const end = React.useMemo(() => {
    if (props.event.windowEnd) {
      return new Date(props.event.windowEnd);
    }
    return null;
  }, [props.event]);

  if (!start || !end) {
    return null;
  }

  if (props.event.type === 'Collection Scheduled' && props.started) {
    return null;
  }

  return (
    <React.Fragment>
      {props.event.type === 'Collection Scheduled' && (
        <P2GGrid item container classNames={classes.textSection}>
          <P2GText variant={props.headingVariant}>
            <Trans t={t} i18nKey="RESULT_WINDOW_COLLECTION_TITLE" defaults="Your estimated collection period" />
          </P2GText>
          <P2GText variant="body1" component="p">
            <Trans
              t={t}
              i18nKey="RESULT_WINDOW_COLLECTION_DESCRIPTION"
              defaults="The courier estimates your collection time to be: {{date}} from {{starttime}} - {{endtime}}"
              options={{
                date: start.toLocaleDateString('en-GB'),
                starttime: start.toLocaleTimeString('en-GB'),
                endtime: end.toLocaleTimeString('en-GB')
              }}
            />
          </P2GText>
        </P2GGrid>
      )}

      {props.event.type === 'Delivery Scheduled' && (
        <P2GGrid item container classNames={classes.textSection}>
          <P2GText variant={props.headingVariant}>
            <Trans t={t} i18nKey="RESULT_WINDOW_DELIVERY_TITLE" defaults="Your estimated delivery period" />
          </P2GText>
          <P2GText variant="body1" component="p">
            <Trans
              t={t}
              i18nKey="RESULT_WINDOW_DELIVERY_DESCRIPTION"
              defaults="The courier estimates your parcel delivery time to be: {{date}} from {{starttime}} - {{endtime}}"
              options={{
                date: start.toLocaleDateString('en-GB'),
                starttime: start.toLocaleTimeString('en-GB'),
                endtime: end.toLocaleTimeString('en-GB')
              }}
            />
          </P2GText>
        </P2GGrid>
      )}
    </React.Fragment>
  );
};

export default TrackingWindow;
